import React, { useState, useEffect } from 'react';
import { translations } from '../translations';
import './AdminAnalytics.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AdminAnalytics() {
  const [userStats, setUserStats] = useState(null);
  const [storyStats, setStoryStats] = useState(null);
  const [revenueStats, setRevenueStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('users');
  const language = localStorage.getItem('language') || 'en';
  const t = translations[language];

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const [usersRes, storiesRes, revenueRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/analytics/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/admin/analytics/stories`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/admin/analytics/revenue`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (!usersRes.ok || !storiesRes.ok || !revenueRes.ok) {
        throw new Error('Failed to fetch analytics');
      }

      const userData = await usersRes.json();
      const storyData = await storiesRes.json();
      const revenueData = await revenueRes.json();

      setUserStats(userData);
      setStoryStats(storyData);
      setRevenueStats(revenueData);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-analytics">
        <h2>📊 {language === 'de' ? 'Admin Analytics' : 'Admin Analytics'}</h2>
        <div className="analytics-loading">Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-analytics">
        <h2>📊 {language === 'de' ? 'Admin Analytics' : 'Admin Analytics'}</h2>
        <div className="analytics-error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="admin-analytics">
      <h2>📊 {language === 'de' ? 'Admin Analytics' : 'Admin Analytics'}</h2>
      
      <div className="analytics-tabs">
        <button
          className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          👥 {language === 'de' ? 'Benutzer' : 'Users'}
        </button>
        <button
          className={`tab-btn ${activeTab === 'stories' ? 'active' : ''}`}
          onClick={() => setActiveTab('stories')}
        >
          📖 {language === 'de' ? 'Geschichten' : 'Stories'}
        </button>
        <button
          className={`tab-btn ${activeTab === 'revenue' ? 'active' : ''}`}
          onClick={() => setActiveTab('revenue')}
        >
          💰 {language === 'de' ? 'Umsatz' : 'Revenue'}
        </button>
      </div>

      {activeTab === 'users' && userStats && (
        <div className="analytics-content">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-label">{language === 'de' ? 'Gesamt Benutzer' : 'Total Users'}</div>
              <div className="stat-value">{userStats.total_users}</div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">🆓</div>
              <div className="stat-label">{language === 'de' ? 'Kostenlose Benutzer' : 'Free Users'}</div>
              <div className="stat-value">{userStats.free_users}</div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">⭐</div>
              <div className="stat-label">{language === 'de' ? 'Pro Benutzer' : 'Pro Users'}</div>
              <div className="stat-value">{userStats.pro_users}</div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">📅</div>
              <div className="stat-label">{language === 'de' ? 'Neue (30 Tage)' : 'New (30 days)'}</div>
              <div className="stat-value">{userStats.recent_registrations}</div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">✅</div>
              <div className="stat-label">{language === 'de' ? 'Verifiziert' : 'Verified'}</div>
              <div className="stat-value">{userStats.verified_users}</div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-label">{language === 'de' ? 'Pro Conversion' : 'Pro Conversion'}</div>
              <div className="stat-value">
                {userStats.total_users > 0 
                  ? `${((userStats.pro_users / userStats.total_users) * 100).toFixed(1)}%`
                  : '0%'}
              </div>
            </div>
          </div>

          {userStats.registrations_by_date && userStats.registrations_by_date.length > 0 && (
            <div className="chart-section">
              <h3>{language === 'de' ? 'Registrierungen (30 Tage)' : 'Registrations (30 days)'}</h3>
              <div className="simple-chart">
                {userStats.registrations_by_date.map((item) => (
                  <div key={item._id} className="chart-bar">
                    <div className="chart-label">{item._id.split('-').slice(1).join('/')}</div>
                    <div className="chart-bar-container">
                      <div 
                        className="chart-bar-fill" 
                        style={{width: `${(item.count / Math.max(...userStats.registrations_by_date.map(i => i.count))) * 100}%`}}
                      >
                        {item.count}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'stories' && storyStats && (
        <div className="analytics-content">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">📚</div>
              <div className="stat-label">{language === 'de' ? 'Gesamt Geschichten' : 'Total Stories'}</div>
              <div className="stat-value">{storyStats.total_stories}</div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">🆓</div>
              <div className="stat-label">{language === 'de' ? 'Kostenlos' : 'Free Tier'}</div>
              <div className="stat-value">{storyStats.free_tier_stories}</div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">⭐</div>
              <div className="stat-label">{language === 'de' ? 'Pro' : 'Pro Tier'}</div>
              <div className="stat-value">{storyStats.pro_tier_stories}</div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">🆕</div>
              <div className="stat-label">{language === 'de' ? 'Neu (7 Tage)' : 'Recent (7 days)'}</div>
              <div className="stat-value">{storyStats.recent_stories}</div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">🇬🇧</div>
              <div className="stat-label">{language === 'de' ? 'Englisch' : 'English'}</div>
              <div className="stat-value">{storyStats.stories_by_language.english}</div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">🇩🇪</div>
              <div className="stat-label">{language === 'de' ? 'Deutsch' : 'German'}</div>
              <div className="stat-value">{storyStats.stories_by_language.german}</div>
            </div>
          </div>

          {storyStats.stories_by_date && storyStats.stories_by_date.length > 0 && (
            <div className="chart-section">
              <h3>{language === 'de' ? 'Geschichten erstellt (30 Tage)' : 'Stories Created (30 days)'}</h3>
              <div className="simple-chart">
                {storyStats.stories_by_date.map((item) => (
                  <div key={item._id} className="chart-bar">
                    <div className="chart-label">{item._id.split('-').slice(1).join('/')}</div>
                    <div className="chart-bar-container">
                      <div 
                        className="chart-bar-fill" 
                        style={{width: `${(item.count / Math.max(...storyStats.stories_by_date.map(i => i.count))) * 100}%`}}
                      >
                        {item.count}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'revenue' && revenueStats && (
        <div className="analytics-content">
          <div className="stats-grid">
            <div className="stat-card highlight">
              <div className="stat-icon">💰</div>
              <div className="stat-label">{language === 'de' ? 'Gesamt Umsatz' : 'Total Revenue'}</div>
              <div className="stat-value">${revenueStats.total_revenue.toFixed(2)}</div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">✅</div>
              <div className="stat-label">{language === 'de' ? 'Aktive Abos' : 'Active Subscriptions'}</div>
              <div className="stat-value">{revenueStats.active_subscriptions}</div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">❌</div>
              <div className="stat-label">{language === 'de' ? 'Abgebrochen' : 'Cancelled'}</div>
              <div className="stat-value">{revenueStats.cancelled_subscriptions}</div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">💳</div>
              <div className="stat-label">{language === 'de' ? 'Stripe Umsatz' : 'Stripe Revenue'}</div>
              <div className="stat-value">${revenueStats.revenue_by_method.stripe.revenue.toFixed(2)}</div>
              <div className="stat-sublabel">
                {revenueStats.revenue_by_method.stripe.count} {language === 'de' ? 'Transaktionen' : 'transactions'}
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">💳</div>
              <div className="stat-label">{language === 'de' ? 'PayPal Umsatz' : 'PayPal Revenue'}</div>
              <div className="stat-value">${revenueStats.revenue_by_method.paypal.revenue.toFixed(2)}</div>
              <div className="stat-sublabel">
                {revenueStats.revenue_by_method.paypal.count} {language === 'de' ? 'Transaktionen' : 'transactions'}
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-label">{language === 'de' ? 'Gesamt Transaktionen' : 'Total Transactions'}</div>
              <div className="stat-value">{revenueStats.paid_transactions}/{revenueStats.total_transactions}</div>
              <div className="stat-sublabel">
                {language === 'de' ? 'Bezahlt/Gesamt' : 'Paid/Total'}
              </div>
            </div>
          </div>

          {revenueStats.revenue_by_date && revenueStats.revenue_by_date.length > 0 && (
            <div className="chart-section">
              <h3>{language === 'de' ? 'Umsatz (30 Tage)' : 'Revenue (30 days)'}</h3>
              <div className="simple-chart">
                {revenueStats.revenue_by_date.map((item) => (
                  <div key={item._id} className="chart-bar">
                    <div className="chart-label">{item._id.split('-').slice(1).join('/')}</div>
                    <div className="chart-bar-container">
                      <div 
                        className="chart-bar-fill revenue" 
                        style={{width: `${(item.revenue / Math.max(...revenueStats.revenue_by_date.map(i => i.revenue))) * 100}%`}}
                      >
                        ${item.revenue.toFixed(2)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminAnalytics;
